import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { dataService } from '../services/dataService';

const DataContext = createContext();

// Data reducer
const dataReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, isLoading: false };
    case 'SET_CONTENT':
      return { ...state, content: action.payload, isLoading: false, error: null };
    case 'SET_REFERENCES':
      return { ...state, references: action.payload, isLoadingReferences: false, referencesError: null };
    case 'SET_REFERENCES_LOADING':
      return { ...state, isLoadingReferences: action.payload };
    case 'SET_REFERENCES_ERROR':
      return { ...state, referencesError: action.payload, isLoadingReferences: false };
    case 'SET_SUBJECTS':
      return { ...state, subjects: action.payload };
    case 'SET_CURRENT_BATCH':
      return { ...state, currentBatch: action.payload };
    case 'SET_FILTERS':
      return { ...state, filters: { ...state.filters, ...action.payload } };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    case 'CLEAR_REFERENCES_ERROR':
      return { ...state, referencesError: null };
    default:
      return state;
  }
};

const initialState = {
  content: [],
  references: [],
  subjects: [],
  currentBatch: '44',
  filters: {
    search: '',
    subject: 'all',
    type: 'all'
  },
  isLoading: false,
  isLoadingReferences: false,
  error: null,
  referencesError: null
};

export const DataProvider = ({ children }) => {
  const [state, dispatch] = useReducer(dataReducer, initialState);

  const isLoadingContentRef = React.useRef(false);
  const isLoadingReferencesRef = React.useRef(false);

  const loadContent = useCallback(async () => {
    // Prevent multiple simultaneous calls
    if (isLoadingContentRef.current) return;

    isLoadingContentRef.current = true;
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const content = await dataService.getContent(state.currentBatch);
      dispatch({ type: 'SET_CONTENT', payload: content });

      // Extract unique subjects
      const subjects = [...new Set(content.map(item => item.subject))].sort();
      dispatch({ type: 'SET_SUBJECTS', payload: subjects });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
    } finally {
      isLoadingContentRef.current = false;
    }
  }, [state.currentBatch]);

  const loadReferences = useCallback(async () => {
    // Prevent multiple simultaneous calls
    if (isLoadingReferencesRef.current) return;

    isLoadingReferencesRef.current = true;
    dispatch({ type: 'SET_REFERENCES_LOADING', payload: true });
    try {
      const references = await dataService.getReferences();
      dispatch({ type: 'SET_REFERENCES', payload: references });
    } catch (error) {
      dispatch({ type: 'SET_REFERENCES_ERROR', payload: error.message });
    } finally {
      isLoadingReferencesRef.current = false;
    }
  }, []);

  useEffect(() => {
    // Load initial data
    loadContent();
    loadReferences();
  }, [loadContent, loadReferences]);

  const refreshContent = async () => {
    await loadContent();
  };

  const refreshReferences = async () => {
    await loadReferences();
  };

  const setBatch = (batch) => {
    dispatch({ type: 'SET_CURRENT_BATCH', payload: batch });
  };

  const setFilters = (filters) => {
    dispatch({ type: 'SET_FILTERS', payload: filters });
  };

  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const clearReferencesError = () => {
    dispatch({ type: 'CLEAR_REFERENCES_ERROR' });
  };

  // Filter content based on current filters
  const filteredContent = state.content.filter(item => {
    const matchesSearch = !state.filters.search ||
      item.title.toLowerCase().includes(state.filters.search.toLowerCase()) ||
      item.subject.toLowerCase().includes(state.filters.search.toLowerCase());

    const matchesSubject = state.filters.subject === 'all' ||
      item.subject === state.filters.subject;

    const matchesType = state.filters.type === 'all' ||
      item.type.toLowerCase() === state.filters.type.toLowerCase();

    return matchesSearch && matchesSubject && matchesType;
  });

  const value = {
    ...state,
    filteredContent,
    loadContent,
    loadReferences,
    refreshContent,
    refreshReferences,
    setBatch,
    setFilters,
    clearError,
    clearReferencesError
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};
